/**
 * Mobile card content contract — what a grid row states on a phone, and how a
 * tier that mounts someone else's grid adds its own facts to that card.
 *
 * A base grid owns the identity of a row: its name, its status, its health
 * chips. A tier mounting that same grid under its own route — a services crew
 * picking the site it is working — needs job facts on the same card without
 * the base grid learning what a job is. Both sides hand in the same sections
 * shape and this module merges them, so the card component stays the single
 * place that decides layout and neither side owns the other's vocabulary.
 *
 * Contains:
 * - selectRowMeta            — presence-guarded row fields → labelled entries
 * - composeMobileCardSections — merge a base card's sections with a tier's
 *
 * Returns plain JS — no JSX.
 */

/** A metadata value is worth a line when it exists and is not the empty string; 0 and false are values. */
const isPresent = value => value !== null && value !== undefined && value !== '';

/**
 * Reads the metadata entries a row can actually support.
 *
 * A field the row does not carry produces no entry at all, so a card mounted
 * against a descriptor that does not emit a given column degrades to the rest
 * of its content rather than rendering an empty label or a fabricated zero.
 *
 * @param {object} row - The grid row payload.
 * @param {{key?: string, field: string, label: string, format?: Function}[]} defs
 *   Field definitions. `key` defaults to `field`; `format(value, row)` shapes
 *   the display value and runs only for fields the row carries.
 * @returns {{key: string, label: string, value: *}[]} Entries in `defs` order,
 *   omitting every field absent from the row.
 */
export function selectRowMeta(row, defs) {
  if (!row || !Array.isArray(defs)) return [];
  return defs
    .filter(def => def && isPresent(row[def.field]))
    .map(def => ({
      key: def.key ?? def.field,
      label: def.label,
      value: def.format ? def.format(row[def.field], row) : row[def.field],
    }))
    .filter(entry => isPresent(entry.value));
}

const normalizeMeta = meta => (Array.isArray(meta) ? meta.filter(entry => entry && isPresent(entry.value)) : []);

const normalizeNodes = nodes => (Array.isArray(nodes) ? nodes.filter(Boolean) : []);

/**
 * Merges a base card's sections with the sections a tier extension supplies.
 *
 * A tier restates a metadata entry by reusing its key: the extension's entry
 * replaces the base one in place of appending a contradicting duplicate.
 * Actions are singular — a tier that supplies them owns the card's action
 * row outright, because two competing action clusters on one card read as
 * two rows.
 *
 * @param {{meta?: Array, chips?: Array, actions?: *}} [base] - Base grid sections.
 * @param {{meta?: Array, chips?: Array, actions?: *}} [extra] - Tier extension sections.
 * @returns {{meta: Array, chips: Array, actions: *}} Merged sections; `meta`
 *   and `chips` are always arrays and `actions` is null when neither side
 *   supplies one.
 */
export function composeMobileCardSections(base, extra) {
  const baseMeta = normalizeMeta(base?.meta);
  const extraMeta = normalizeMeta(extra?.meta);
  const restated = new Set(extraMeta.map(entry => entry.key));

  return {
    meta: [...baseMeta.filter(entry => !restated.has(entry.key)), ...extraMeta],
    chips: [...normalizeNodes(base?.chips), ...normalizeNodes(extra?.chips)],
    actions: extra?.actions ?? base?.actions ?? null,
  };
}
